import { Navigate, Route, Routes, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './auth/store.js';
import { LoginPage } from './pages/Login.js';
import { ProjectsPage } from './pages/Projects.js';
import { ProjectDetailPage } from './pages/ProjectDetail.js';
import { ProjectSettingsPage } from './pages/ProjectSettings.js';
import { PlatformSettingsPage } from './pages/PlatformSettings.js';
import { PendingUsersPage } from './pages/PendingUsers.js';
import { AccountPage } from './pages/Account.js';
import { ChapterEditorPage } from './pages/ChapterEditor.js';
import { api } from './api/client.js';

export function App() {
  const { user, refreshToken, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const location = useLocation();

  if (!user) {
    return <LoginPage />;
  }

  // The chapter editor needs all the width it can get for the screenshot
  // canvas — don't cap it at the reading-width used by the list pages.
  const isEditor = location.pathname.startsWith('/chapters/');
  const isSuperAdmin = user.roles.includes('super_admin');
  const canManageUsers = isSuperAdmin || user.roles.includes('training_admin');

  return (
    <div className="app">
      <header className="topbar">
        <div className="row" style={{ gap: 24 }}>
          <h1>{t('app.title')}</h1>
          <nav>
            <Link to="/projects">{t('nav.projects')}</Link>
            {canManageUsers && <Link to="/pending-users">{t('pendingUsers.title')}</Link>}
            {isSuperAdmin && <Link to="/platform-settings">{t('nav.platformSettings')}</Link>}
          </nav>
        </div>
        <div className="right">
          <select
            value={i18n.language.split('-')[0]}
            onChange={(e) => void i18n.changeLanguage(e.target.value)}
            style={{ width: 80 }}
          >
            <option value="nl">NL</option>
            <option value="en">EN</option>
          </select>
          <Link to="/account" className="muted">
            {user.displayName}
          </Link>
          <button
            className="secondary"
            onClick={async () => {
              if (refreshToken) await api.logoutCall(refreshToken).catch(() => {});
              logout();
              nav('/');
            }}
          >
            {t('auth.logout')}
          </button>
        </div>
      </header>
      <main className={isEditor ? 'main main-editor' : 'main'}>
        <Routes>
          <Route path="/" element={<Navigate to="/projects" replace />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
          <Route path="/projects/:projectId/settings" element={<ProjectSettingsPage />} />
          <Route
            path="/platform-settings"
            element={isSuperAdmin ? <PlatformSettingsPage /> : <Navigate to="/projects" replace />}
          />
          <Route
            path="/pending-users"
            element={canManageUsers ? <PendingUsersPage /> : <Navigate to="/projects" replace />}
          />
          <Route path="/chapters/:chapterId" element={<ChapterEditorPage />} />
          <Route path="*" element={<Navigate to="/projects" replace />} />
        </Routes>
      </main>
    </div>
  );
}
